import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import beFindVoucher from 'actions/voucher/beFindVoucher.action';
import { allFieldsAmount, allFieldsUsedAmount } from 'bl/fields.bl';
import CurrencySign from 'components/price/CurrencySign.component';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCheckoutDataVouchersUsed } from 'selectors/checkout.selectors';
import asArray from 'utils/arrays/asArray.util';
import createFullArray from 'utils/arrays/createFullArray.util';
import shallowCopyArray from 'utils/arrays/shallowCopyArray.util';
import sumArray from 'utils/arrays/sumArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import { updateVouchersUsed } from 'checkout/actions';
import Voucher from 'checkout/components/voucher/Voucher.component';

import CN from './CheckoutVoucher.module.scss';

const L = logger('CheckoutVoucher');

// before you try to calculate the sums
// extract the fields and convert them to number
// they might be strings, empty strings or give you NaN after conversion, so default to 0
const convert = (string) => fromAnyToNumber(string) || 0;

const CheckoutVoucher = ({
  clientId,
  total,
  updateVouchersUsed,
  vouchersUsed: vouchersUsedFromProps,
  beFindVoucher,
}) => {
  const [size, setSize] = useState(0);
  const [voucherAmount, setVoucherAmount] = useState(0);
  const [voucherAmountUsed, setVoucherAmountUsed] = useState(0);
  const [warning, setWarning] = useState('');
  const { val: isVoucherShown, yay: showVoucher } = useTip(false);
  const [voucherList, setVoucherList] = useState([]);

  const { t } = useTranslation();

  const vouchersUsed = asArray(vouchersUsedFromProps);
  const displayAvailable = voucherAmount || 0;
  const displayUsed = voucherAmountUsed || 0;

  const handleFound = useCallback(
    async (ev) => {
      try {
        L.debug('handleFound()', ev);

        const result = await beFindVoucher({ number: ev?.number });
        const voucher = result?.json;
        const success = fromAnyToBool(voucher?.id);

        const vs = [...voucherList];

        if (success) {
          vs[ev?.index] = asObject(voucher);
          setVoucherList(vs);
        }

        L.debug('handleFound()', { success, voucher, vs, voucherList });

        return { success };
      } catch (e) {
        L.warn('handleFind()', e);
      }
    },
    [beFindVoucher, voucherList],
  );

  const handleShow = useCallback(() => {
    showVoucher();
    setSize(size + 1);
  }, [size, showVoucher]);

  const handleChange = useCallback(
    ({ value, number, index, ...extra }) => {
      const FTAG = 'handleChange()';

      const currentVoucher = voucherList[index];

      L.debug(FTAG, { index, value, number, voucherAmount, currentVoucher }, extra);

      if (number > voucherAmount) return void setWarning(t('checkout.giftVouchers.warningMsg'));

      setWarning('');

      if (!currentVoucher) return void L.warn(FTAG, 'Aborting due to negative currentVoucher:', currentVoucher);

      const vouchersUsedCopy = shallowCopyArray(vouchersUsed);
      vouchersUsedCopy[index] = { voucher: currentVoucher.id, used_amount: number || 0, used_by_clientId: clientId };

      updateVouchersUsed(vouchersUsedCopy);
    },
    [clientId, t, updateVouchersUsed, voucherAmount, voucherList, vouchersUsed],
  );

  useEffect(() => {
    const convertedVouchers = allFieldsAmount(voucherList).map(convert);
    const convertedUsed = allFieldsUsedAmount(vouchersUsed).map(convert);

    const summedVouchers = sumArray(convertedVouchers);
    const summedUsed = sumArray(convertedUsed);

    L.debug(
      'useEffect()',
      { voucherList, vouchersUsed },
      { convertedVouchers, convertedUsed },
      { summedVouchers, summedUsed },
    );

    setVoucherAmount(summedVouchers);
    setVoucherAmountUsed(summedUsed);
  }, [voucherList, vouchersUsed]);

  L.debug('()', { displayAvailable, displayUsed }, { voucherList, vouchersUsedFromProps });

  return (
    <div data-bem="CheckoutVoucher" className={cn('table-checkout tbl-vouchers', CN.component)}>
      {size > 0 && (
        <div data-bem="CheckoutVoucher__head" className={cn('thead', CN.head)}>
          <div className="cell-name">{t('checkout.giftVouchers.title')}</div>
          <div className="cell-use text-r">
            {t('checkout.giftVouchers.available')} <CurrencySign value={displayAvailable} />
          </div>
          <div className="cell-used text-r">
            {t('checkout.giftVouchers.used')} <CurrencySign value={displayUsed} />
          </div>
          <div className="cell-empty" />
        </div>
      )}

      {isVoucherShown && (
        <div data-bem="CheckoutVoucher__body" className="tbody">
          {createFullArray({ size, indices: true }).map((e, index) => (
            <Voucher
              key={createNodeKey({ index, tag: e })}
              index={index}
              onFound={handleFound}
              onVoucherAmountChange={handleChange}
              total={total}
              vouchers={voucherList}
              warning={warning}
            />
          ))}
        </div>
      )}

      <button className="btn-use-voucher" onClick={handleShow}>
        {isVoucherShown ? t('checkout.giftVouchers.useAnotherVoucher') : t('checkout.giftVouchers.useVoucher')}
      </button>
    </div>
  );
};

const mapStateToProps = (state) => ({
  vouchersUsed: selectCheckoutDataVouchersUsed(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ beFindVoucher, updateVouchersUsed }, dispatch);

const CheckoutVoucherHoc = connect(mapStateToProps, mapDispatchToProps)(CheckoutVoucher);

export default CheckoutVoucherHoc;
